import { useLazyGetRimsQuery } from '@/api/rims'
import RimFilter from '@/components/filter/RimFilter/RimFilter'
import DefaultInput from '@/components/inputs/DefaultInput/DefaultInput'
import VehicleSelector from '@/components/vehicle/VehicleSelector/VehicleSelector'
import { useTranslation } from 'next-i18next'
import React, { useCallback, useEffect } from 'react'
import SearchResult from '../SearchResult/SearchResult'
import styles from './RimSearch.module.scss'
import { resetRimDestination, selectRimsCriteria, setRimsCriteria } from '@/features/search/searchSlice'
import { useAppSelector } from '@/utils/hooks/useAppSelector'
import { useAppDispatch } from '@/utils/hooks/useAppDispatch'
import { ArticleModel, RimSearchCriteraModel } from '@/types/api'
import { resetVehicle, selectBrand, selectLicensePlate, selectLicensePlateCountryCode, selectLicensePlateValid } from '@/features/vehicle/vehicleSlice'
import Button from '@/components/buttons/Button/Button'
import useAnalyticsManager from '@/utils/hooks/useAnalyticsManager'
import LicensePlate from '@/components/vehicle/LicensePlate/LicensePlate'
import usePlateSearchArrival from '@/utils/hooks/usePlateSearchArrival'
import NoCarFound from '@/components/common/NoCarFound/NoCarFound'
import VehicleInfo from '@/components/vehicle/VehicleInfo/VehicleInfo'
import RimSearchQueryParams from '@/types/RimSearchQueryParams'
import useUpdateQuery from '@/utils/hooks/useUpdateQuery'
import { filterObject } from '@/utils/helpers'
import { useRouter } from 'next/router'
import SearchStep from '@/types/SearchStep'
import FilterChangeHandler from '@/types/FilterChangeHandler'

type Props = {
  onHasSearch?: (hasResult: boolean) => void
  onVehicleChange?: () => void
}

const RimSearch = (props: Props) => {
  const [trigger, result] = useLazyGetRimsQuery()
  const [filterdItems, setFilterdItems] = React.useState<any[]>()
  const [step, setStep] = React.useState<SearchStep>(SearchStep.Search)
  const queryLoaded = React.useRef<boolean>(false)
  const { i18n, t } = useTranslation()
  const rimsSearchCriteria = useAppSelector(selectRimsCriteria)
  const selectedVehicleBrand = useAppSelector(selectBrand)
  const dispatch = useAppDispatch()
  const analyticsManager = useAnalyticsManager()
  const licensePlate = useAppSelector(selectLicensePlate)
  const licensePlateValid = useAppSelector(selectLicensePlateValid)
  const licensePlateCountryCode = useAppSelector(selectLicensePlateCountryCode)
  const { updateQuery, resetQuery } = useUpdateQuery<RimSearchQueryParams>()
  const router = useRouter()

  const buildQuery = useCallback((criteria: RimSearchCriteraModel): RimSearchQueryParams => {
    const params: RimSearchQueryParams = {
      licenseplate: criteria?.licensePlate,
      licensecountrycode: criteria?.licenseCountryCode,
      ktype: criteria?.kTypeId?.toString(),
      description: criteria?.description,
    }

    return filterObject(params)
  }, [])

  const rebuildQuery = useCallback((criteria: RimSearchCriteraModel, overwriteQuery?: boolean) => {
    resetQuery()
    let query = buildQuery(criteria)
    updateQuery(query, overwriteQuery)
  }, [buildQuery, resetQuery, updateQuery])

  const triggerSearch = useCallback((criteria: RimSearchCriteraModel, overwriteQuery?: boolean) => {
    dispatch(setRimsCriteria(criteria))
    setFilterdItems(undefined)
    trigger(criteria)
    analyticsManager.search(criteria.description)

    rebuildQuery(criteria, overwriteQuery)

    if (props.onHasSearch)
      props.onHasSearch(true)
  }, [analyticsManager, dispatch, props, rebuildQuery, trigger])

  const handleOnSelectVehicle = (kTypeId: number) => {
    triggerSearch({
      kTypeId,
      licensePlate: '',
      licenseCountryCode: '',
      description: '',
      languageCode: i18n.language,
    }, true)
  }

  const handleOnChangeSearch = (value: string, name: string) => {
    if (selectedVehicleBrand !== undefined)
      dispatch(resetVehicle())

    resetResult()

    dispatch(setRimsCriteria({
      licensePlate: '',
      kTypeId: null,
      description: value,
      languageCode: i18n.language,
    }))
  }

  const searchByText = () => {
    if (rimsSearchCriteria?.description === undefined && !licensePlateValid)
      return

    triggerSearch({
      description: rimsSearchCriteria?.description || '',
      languageCode: i18n.language,
      licensePlate: licensePlate || '',
      licenseCountryCode: licensePlateCountryCode,
    }, true)
  }

  const onFilterChange: FilterChangeHandler<ArticleModel> = (items, query) => {
    updateQuery(query)
    setFilterdItems(items)
  }

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    searchByText()
  }

  const triggerLicensePlateSearch = () => {
    if (licensePlateValid) {
      triggerSearch({
        description: '',
        languageCode: i18n.language,
        licensePlate: licensePlate || '',
        licenseCountryCode: licensePlateCountryCode,
      }, true)
    }
  }

  usePlateSearchArrival(triggerLicensePlateSearch)

  const onSelectedBrand = (brandId: number) => {
    dispatch(resetRimDestination())
    resetResult()

    if (props.onVehicleChange)
      props.onVehicleChange()
  }

  const resetCriteria = () => {
    if (selectedVehicleBrand !== undefined || licensePlate !== undefined)
      dispatch(resetVehicle())

    resetQuery()
    resetResult()
    dispatch(setRimsCriteria(null))
  }

  const resetResult = () => {
    setStep(SearchStep.Search)
    setFilterdItems(undefined)
  }

  const updateStateFromQuery = useCallback((query: RimSearchQueryParams) => {
    if (step === SearchStep.Search && queryLoaded.current)
      return {}

    const newCriteria = {
      licensePlate: query.licenseplate || '',
      licenseCountryCode: query.licensecountrycode || '',
      kTypeId: query.ktype ? parseInt(query.ktype) : undefined,
      description: query.description || '',
      languageCode: i18n.language,
    }

    if (newCriteria.licensePlate || newCriteria.kTypeId || newCriteria.description)
      triggerSearch(newCriteria)

    queryLoaded.current = true
  }, [i18n.language, step, triggerSearch])

  /**
   * Load query from URL and update state
   */
  useEffect(() => {
    if (!queryLoaded.current && router.isReady) {
      updateStateFromQuery(router.query)
    }
  }, [queryLoaded, router.isReady, router.query, updateStateFromQuery])

  useEffect(() => {
    const searchComplete = result.data?.articles && !result.isFetching
    if (searchComplete || result.isFetching) {
      setStep(SearchStep.Result)
    }
  }, [result.data, result.isFetching])

  const settings = {
    showBrand: true,
    showModel: false,
    showTyreInfo: false,
  }

  const items = filterdItems || result.data?.articles
  const searchComplete = result.data?.articles && !result.isFetching && step === SearchStep.Result

  return (
    <div className={styles.component}>
      <form className={styles.searchForm} onSubmit={onSubmit}>
        <LicensePlate />
        <div className={styles.searchInput}>
          <DefaultInput
            searchIcon={true}
            name="search"
            placeholder={t('search.searchPlaceholderRim') || ''}
            onChange={handleOnChangeSearch}
            value={rimsSearchCriteria?.description}
            inputStyle="lightBackground"
          />
          <Button
            label={t('search.searchButton') || ''}
            rounded={true}
            type="submit"
          />
          <Button
            label={t('common:reset') || ''}
            dark={true}
            rounded={true}
            onClick={resetCriteria}
          />
        </div>
      </form>
      <div className={styles.vehiclePicker}>
        <VehicleSelector
          onSelectVehicle={handleOnSelectVehicle}
          onBrandSelected={onSelectedBrand}
          hideSelectors={false}
        />
        {
          result.data?.carInfo && searchComplete && (
            <VehicleInfo
              info={result.data.carInfo}
            />
          )
        }
        {searchComplete && (
          <RimFilter
            items={result.data?.articles || []}
            onFilterChange={onFilterChange}
          />
        )}
      </div>
      {step === SearchStep.Result && (
        <SearchResult
          isFetching={result.isFetching}
          items={items}
          settings={settings}
          scrollToResult={!!rimsSearchCriteria?.description?.length}
          listId={'search_rims'}
          listName={'Search rims'}
          metaData={{
            kType: result?.data?.carInfo?.kTypeId,
            licensePlate: rimsSearchCriteria?.licensePlate,
          }}
        />
      )}
      {result.isError && (
        <NoCarFound
          onClickCarPicker={resetCriteria}
        />
      )}
    </div>
  )
}

export default RimSearch